import { useEffect, useMemo, useState } from "react";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { useAuth } from "../lib/auth.jsx";

function plainNotificationText(value = "") {
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(String(value), "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function NotificationsScreen() {
  const { authedFetch } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expandedId, setExpandedId] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const readCount = useMemo(() => items.filter((n) => n.read_at).length, [items]);

  async function refresh() {
    setLoading(true);
    setErr("");
    try {
      const out = await authedFetch(`/api/notifications/me?limit=100${includeArchived ? "&includeArchived=true" : ""}`);
      setItems(out.notifications || []);
      setUnread(out.unread || 0);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [includeArchived]);

  async function run(label, action) {
    setBusy(label);
    setErr("");
    try {
      await action();
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  }

  async function markOne(notification) {
    if (!notification?.id || notification.read_at) return;
    await authedFetch(`/api/notifications/${notification.id}/read`, { method: "POST" });
    setItems((rows) => rows.map((row) => row.id === notification.id ? { ...row, read_at: new Date().toISOString() } : row));
    setUnread((count) => Math.max(0, count - 1));
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader
          title="Notifications"
          subtitle="Review appointment, payment, aftercare, and studio updates in one inbox."
        />

        {err && <div role="alert" style={alertStyle()}>{err}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 12, marginBottom: 18 }}>
          <Metric label="Unread" value={unread} />
          <Metric label="Read" value={readCount} />
          <Metric label="Shown" value={items.length} />
        </div>

        <Card style={{ maxWidth: "none", padding: 0, overflow: "hidden" }}>
          <div style={{ padding: 18, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", borderBottom: "1px solid var(--border)" }}>
            <div>
              <h3 style={{ margin: 0, color: "var(--text)", fontFamily: "var(--font-display)", fontSize: 22 }}>Inbox</h3>
              <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13 }}>Unread, read, and archived controls match the notification bell.</p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button size="sm" variant="outline" label="Mark read" loading={busy === "read"} onClick={() => run("read", () => authedFetch("/api/notifications/read-all", { method: "POST" }))} />
              <Button size="sm" variant="outline" label="Clear read" loading={busy === "clear-read"} onClick={() => run("clear-read", () => authedFetch("/api/notifications/archive-read", { method: "POST" }))} />
              <Button size="sm" variant="danger" label="Clear all" loading={busy === "clear-all"} onClick={() => {
                if (!window.confirm("Clear all notifications from this inbox?")) return;
                run("clear-all", () => authedFetch("/api/notifications/archive-all", { method: "POST" }));
              }} />
            </div>
          </div>

          <label style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 18px",
            borderBottom: "1px solid var(--border)",
            color: "var(--text-muted)",
            fontSize: 13,
            fontWeight: 700,
          }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Include archived notifications
          </label>

          {loading ? (
            <div style={emptyStyle()}>Loading notifications...</div>
          ) : items.length === 0 ? (
            <div style={emptyStyle()}>You are all caught up.</div>
          ) : (
            <div style={{ display: "grid" }}>
              {items.map((n) => {
                const expanded = expandedId === n.id;
                return (
                  <article key={n.id} style={{
                    padding: 16,
                    borderBottom: "1px solid var(--border)",
                    background: n.read_at ? "transparent" : "rgba(196,135,154,.08)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedId(expanded ? "" : n.id);
                          markOne(n);
                        }}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          textAlign: "left",
                          border: "none",
                          background: "transparent",
                          color: "var(--text)",
                          padding: 0,
                          cursor: "pointer",
                        }}
                      >
                        <strong style={{ display: "block", fontSize: 14 }}>{n.subject || "Notification"}</strong>
                        {n.body && (
                          <span style={{
                            display: "-webkit-box",
                            WebkitLineClamp: expanded ? "unset" : 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            color: "var(--text-muted)",
                            fontSize: 13,
                            lineHeight: 1.55,
                            marginTop: 6,
                          }}>
                            {plainNotificationText(n.body)}
                          </span>
                        )}
                        <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11, marginTop: 8 }}>
                          {n.sent_at ? new Date(n.sent_at).toLocaleString() : ""}
                        </span>
                      </button>
                      <Button
                        size="sm"
                        variant="neutral"
                        label={n.archived_at ? "Archived" : "Clear"}
                        disabled={Boolean(n.archived_at) || busy === n.id}
                        loading={busy === n.id}
                        onClick={() => run(n.id, () => authedFetch(`/api/notifications/${n.id}/archive`, { method: "POST" }))}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <Card style={{ maxWidth: "none", padding: 18 }}>
      <div style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ color: "var(--text)", fontSize: 26, fontWeight: 900, marginTop: 6 }}>{value}</div>
    </Card>
  );
}

function alertStyle() {
  return {
    background: "rgba(180, 50, 50, 0.10)",
    color: "#B42318",
    border: "1px solid rgba(180, 50, 50, 0.22)",
    borderRadius: 14,
    padding: 12,
    marginBottom: 16,
    fontWeight: 700,
  };
}

function emptyStyle() {
  return { padding: 32, textAlign: "center", color: "var(--text-muted)" };
}
